/**
 * This HOC is intended to wrap a component whose root node
 * has a title attribute. This converts the title attribute
 * from the default browser tooltip to a pretty one.
 */

import React from 'react';
import _ from 'lodash';
import { findDOMNode, render, unmountComponentAtNode } from 'react-dom';

import { isInRect } from '../util/DOMUtil';
import { isTouchDevice } from '../util/ScreenUtil';

import './StyledTitle.css';

let _tooltips = [];
let _mouseX = 0;
let _mouseY = 0;

const manager = {
  add(component, node) {
    _tooltips.forEach((t) => {
      t.component.hideTooltip(true);
    });

    _tooltips.push({ component, node, timestamp: Date.now() });
  },
  remove(component) {
    for (let i = 0; i < _tooltips.length; i++) {
      const tooltip = _tooltips[i];
      if (tooltip.component === component) {
        _tooltips.splice(i, 1);
        break;
      }
    }
  },
  cleanup() {
    _tooltips.forEach((t) => {
      const diff = Date.now() - t.timestamp;
      if (diff < 4000) {
        return;
      }

      t.component.hideTooltip(true);
    });

    // After running through the normal remove process, check the dom
    // for orphan tooltips.
    const validNodes = _tooltips.map((t) => t.node.firstChild);
    const invalidNodes = [...document.querySelectorAll('[name="StyledTitle"]')].filter(
      (n) => !_.includes(validNodes, n)
    );
    if (invalidNodes.length) {
      console.log('[StyledTitle] orphaned tooltips: ', invalidNodes);
    }

    invalidNodes.forEach((node) => {
      const parent = node.parentNode;
      if (!parent) {
        return;
      }

      const grandparent = parent.parentNode;
      if (grandparent) {
        grandparent.removeChild(parent);
      } else {
        parent.removeChild(node);
      }
    });
  }
};

setInterval(manager.cleanup, 3000);

document.addEventListener('mousemove', (e) => {
  _mouseX = e.clientX;
  _mouseY = e.clientY;
});

const StyledTitle = (Component) =>
  class StyledTitle extends React.Component {
    static WrappedComponent = Component;

    _div = null;
    _showTimer = null;
    _hideTimer = null;
    _node = null;
    _x = 0;
    _y = 0;
    _isShown = false;
    _initialized = false;

    componentDidMount() {
      this.init();
    }

    componentDidUpdate() {
      if (this._initialized) {
        const node = this._node;
        if (node) {
          const title = node.getAttribute('title');
          if (title && title !== node.dataset.title) {
            node.dataset.title = title;
            node.removeAttribute('title');
          }
        }
      } else {
        this.init();
      }
    }

    componentWillUnmount() {
      if (!this._initialized) {
        return;
      }

      clearTimeout(this._showTimer);
      clearTimeout(this._hideTimer);

      this.hideTooltip(true);

      if (isTouchDevice()) {
        this._node.removeEventListener('touchstart', this.onTouchStart);
        this._node.removeEventListener('touchend', this.onTouchEnd);
        this._node.addEventListener('touchcancel', this.onTouchEnd);
      } else {
        this._node.removeEventListener('mouseenter', this.onMouseEnter);
        this._node.removeEventListener('mousemove', this.onMouseMove);
        this._node.removeEventListener('mouseleave', this.onMouseLeave);
      }
    }

    init = () => {
      const node = (this._node = findDOMNode(this));
      if (!node) {
        return;
      }

      const title = node.getAttribute('title');
      if (!title) {
        return;
      }

      node.dataset.title = title;
      node.removeAttribute('title');
      if (isTouchDevice()) {
        node.addEventListener('touchstart', this.onTouchStart);
        node.addEventListener('touchend', this.onTouchEnd);
        node.addEventListener('touchcancel', this.onTouchEnd);
      } else {
        node.addEventListener('mouseenter', this.onMouseEnter);
        node.addEventListener('mousemove', this.onMouseMove);
        node.addEventListener('mouseleave', this.onMouseLeave);
      }

      this._initialized = true;
    };

    onMouseEnter = (e) => {
      clearTimeout(this._hideTimer);
      const { title } = e.target.dataset;
      this._showTimer = setTimeout(this.showTooltip.bind(this, title), 750);
    };

    onMouseMove = (e) => {
      this._x = e.clientX;
      this._y = e.clientY;
    };

    onMouseLeave = () => {
      clearTimeout(this._showTimer);
      this._hideTimer = setTimeout(this.hideTooltip, 250);
    };

    onTouchStart = (e) => {
      const title = e.target.dataset.title || e.currentTarget.dataset.title;
      const touch = e.touches[0];
      this._x = Math.round(touch.clientX);
      this._y = Math.round(touch.clientY);
      this.showTooltip(title);
    };

    onTouchEnd = () => {
      this.hideTooltip(true);
    };

    showTooltip = (title) => {
      const left = this._x + 10;
      const top = this._y + 10;
      const whiteSpace = title.indexOf('\n') === -1 ? 'nowrap' : 'pre-line';
      const div = (this._div = document.createElement('div'));

      document.body.appendChild(div);

      this._isShown = true;

      manager.add(this, div);

      render(
        <span name="StyledTitle" style={{ top, left, whiteSpace }}>
          {title}
        </span>,
        div,
        this.adjustStyle
      );
    };

    adjustStyle = () => {
      if (!this._div) {
        return;
      }

      const node = this._div.firstChild;
      if (!node) {
        return;
      }

      const rect = node.getBoundingClientRect();

      // By default we try to show the tooltip on one line. But
      // if it gets too wide then we need to allow wrapping.
      if (rect.width > 400 && node.style.whiteSpace === 'nowrap') {
        node.style.whiteSpace = '';
        // Give it a moment then maybe adjust the top/left.
        setTimeout(this.adjustStyle, 100);
      }

      if (rect.right > window.innerWidth) {
        node.style.right = '1px';
        node.style.left = '';
      }

      if (rect.bottom > window.innerHeight) {
        node.style.bottom = '1px';
        node.style.top = '';
      }
    };

    hideTooltip = (force = false) => {
      if (!this._isShown) {
        manager.remove(this);
        return;
      }

      if (!force && isInRect(this._node.getBoundingClientRect(), _mouseX, _mouseY)) {
        this._hideTimer = setTimeout(this.hideTooltip, 250);
        return;
      }

      this._isShown = false;
      unmountComponentAtNode(this._div);
      manager.remove(this);
      try {
        document.body.removeChild(this._div);
      } catch (err) {}
    };

    render() {
      return <Component {...this.props} />;
    }
  };

export default StyledTitle;
